import React, { useImperativeHandle } from 'react';
import { ICaptchaImperativeActions } from '../../Captcha/Captcha.types';
import { classes } from './FormSection.st.css';
import {
  ButtonPriority,
  StatesButton,
  StatesButtonStates,
} from 'wix-ui-tpa/cssVars';
import { useFormSectionSettings } from './useFormSectionSettings';
import { FormSectionProps, FormValidationResult } from './FormSection.types';
import Captcha from '../../Captcha/viewer/Captcha';
import { ForcedState, TEXT_DIRECTION } from '../../../constants';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';

export interface FormSectionImperativeActions {
  resetCaptcha: () => void;
}

const useRecaptcha = () => {
  const [token, setToken] = React.useState('');
  const ref = React.useRef<ICaptchaImperativeActions>(null);

  return {
    token,
    setToken,
    ref,
  };
};

export const FormSection = React.forwardRef<
  FormSectionImperativeActions,
  FormSectionProps
>(
  (
    {
      children: formFields,
      formType,
      isLoading,
      onSubmit,
      isEditor,
      isPreview,
      forceView,
      showCaptcha,
      onValidationComplete,
      validateFormFields,
    },
    ref,
  ) => {
    const settings = useFormSectionSettings({ formType });
    const [captchaError, setCaptchaError] = React.useState<string>('');
    const { isRTL } = useEnvironment();

    const recaptcha = useRecaptcha();
    const { i18n, t } = useTranslation();

    // Expose imperative handle for resetting captcha
    useImperativeHandle(ref, () => ({
      resetCaptcha: () => {
        recaptcha.ref.current?.reset();
        recaptcha.setToken('');
        setCaptchaError('');
      },
    }));

    // Determine if editor state is forcing captcha error display for preview
    const isCaptchaErrorForcedByEditor =
      isEditor && forceView === ForcedState.INPUT_FIELDS_ERROR;

    // Get the appropriate error message
    const displayCaptchaError = isCaptchaErrorForcedByEditor
      ? t('memberAuthentication.uouScreens.recaptcha.error')
      : captchaError;

    /**
     * Comprehensive validation that validates all fields simultaneously
     */
    const validateAllFields = (): FormValidationResult => {
      let isValid = true;
      let validationCaptchaError: string | undefined;
      let formFieldsError: string | undefined;

      // 1. Validate captcha if shown (only in non-editor mode)
      if (!isEditor && showCaptcha && !recaptcha.token) {
        validationCaptchaError = t(
          'memberAuthentication.uouScreens.recaptcha.error',
        );
        setCaptchaError(validationCaptchaError as string);
        isValid = false;
      } else {
        setCaptchaError('');
      }

      // 2. Validate form fields using provided validation function
      if (validateFormFields) {
        const formValidation = validateFormFields();
        if (!formValidation.isValid) {
          // Individual field errors are handled by FormInputs component
          // We just track that form fields validation failed
          formFieldsError = 'Please fix the field errors above';
          isValid = false;
        }
      }

      return {
        isValid,
        captchaError: validationCaptchaError,
        formFieldsError,
      };
    };

    const handleSubmit = () => {
      // Perform comprehensive validation
      const validationResult = validateAllFields();

      // Notify parent about validation results
      if (onValidationComplete) {
        onValidationComplete(validationResult);
      }

      // Only proceed with submission if everything is valid
      if (validationResult.isValid) {
        onSubmit({ recaptchaToken: recaptcha.token });
      }
    };

    // Clear captcha error when token is received (only in non-editor mode)
    React.useEffect(() => {
      if (!isEditor && recaptcha.token && captchaError) {
        setCaptchaError('');
      }
    }, [isEditor, recaptcha.token, captchaError]);

    const getSubmitButtonClassName = () => {
      const baseClass =
        formType === 'login'
          ? classes.loginSubmitButton
          : classes.signupSubmitButton;
      const hoverClass =
        isEditor && forceView === ForcedState.SUBMIT_BUTTON_HOVER
          ? formType === 'login'
            ? classes.loginHover
            : classes.signupHover
          : '';

      return `${baseClass} ${hoverClass}`.trim();
    };

    return (
      <div
        data-hook={`${formType}-form-section`}
        className={classes.formSection}
      >
        <div
          data-hook={`${formType}-form-fields`}
          className={classes.formInputs}
        >
          {formFields}
        </div>
        {showCaptcha && (
          <div
            data-hook={`${formType}-recaptcha`}
            className={classes.recaptcha}
          >
            <Captcha
              language={i18n.language}
              id={`${formType}-captcha`}
              token={recaptcha.token}
              onTokenChange={(token = '') => recaptcha.setToken(token)}
              isMobileFriendly={false}
              ref={recaptcha.ref}
            />
            {displayCaptchaError && (
              <div
                data-hook={`${formType}-captcha-error`}
                className={classes.captchaError}
                dir={isRTL ? TEXT_DIRECTION.RTL : TEXT_DIRECTION.LTR}
              >
                {displayCaptchaError}
              </div>
            )}
          </div>
        )}
        <StatesButton
          data-hook={`${formType}-submit-button`}
          onClick={isPreview ? undefined : handleSubmit}
          disabled={isLoading}
          className={getSubmitButtonClassName()}
          priority={ButtonPriority.primary}
          state={StatesButtonStates.IDLE}
          idleContent={settings.submitButtonText ?? ''}
          dir={isRTL ? TEXT_DIRECTION.RTL : TEXT_DIRECTION.LTR}
        >
          {isLoading
            ? 'Logging in...' // TODO: How to handle this content?
            : settings.submitButtonText}
        </StatesButton>
      </div>
    );
  },
);

FormSection.displayName = 'FormSection';
